// StorePredictiveReport.jsx
// ✅ Supplier required, default first selected
// ✅ Supplier filter sends supplierCode
// ✅ Brand optional, default null
// ✅ Brand filter sends brand name
// ✅ Branch filter optional
// ✅ Pagination + sorting + CSV export + Excel export
// ✅ Currency from redux

import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import '../report.css';
import ApiService from '../../../../services/apiService';
import ApiService2 from '../../../../../INVENTORY/services/apiService';
import * as XLSX from 'xlsx';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';

const numberFmt = (n, digits = 2) =>
  new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(Number.isFinite(+n) ? +n : 0);

const toNumber = (val) => {
  const n = Number(val);
  return Number.isFinite(n) ? n : 0;
};

const COLUMNS = [
  { key: 'itemNo', label: 'Item No' },
  { key: 'itemName', label: 'Item Name' },
  { key: 'quantityInHand', label: 'Quantity In Hand', align: 'right' },
  { key: 'qtySoldLast3Months', label: 'Qty Sold Last 3 Months', align: 'right' },
  { key: 'predictiveInventoryForMonth', label: 'Predictive Inventory For Month', align: 'right' },
];

const PAGE_SIZE_OPTIONS = [
  { value: 10, label: '10' },
  { value: 25, label: '25' },
  { value: 50, label: '50' },
  { value: 100, label: '100' },
  { value: 500, label: '500' },
  { value: 1000, label: '1000' },
];

const selectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 999999 }),
  menu: (base) => ({ ...base, zIndex: 999999 }),
};

const StorePredictiveReport = ({ Currency }) => {
  const navigate = useNavigate();
  const currencyCode = Currency?.currency?.code || '';

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);

  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);

  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);

  const [sortState, setSortState] = useState({
    key: 'itemName',
    dir: 'asc',
  });

  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 100,
    totalRecords: 0,
    totalPages: 0,
    hasPrevious: false,
    hasNext: false,
  });

  // ---------- FETCH BRANCHES ----------
  const fetchBranches = async () => {
    try {
      const apiService = new ApiService();
      const res = await apiService.get('GET_BRANCH');
      const list = Array.isArray(res?.data?.result) ? res.data.result : [];

      const mapped = list
        .map((b) => ({
          value: b?.id,
          label: String(b?.branchName || '').trim(),
        }))
        .filter((x) => x.value != null && x.label);

      setBranches(mapped);
    } catch (e) {
      console.error('Failed to fetch branches', e);
      setBranches([]);
    }
  };

  // ---------- FETCH SUPPLIERS ----------
  const fetchSuppliers = async () => {
    try {
      const apiService = new ApiService2();
      const res = await apiService.get('GET_SUPPLIER');
      const list = Array.isArray(res?.data?.result) ? res.data.result : [];

      const mapped = list
        .map((s) => ({
          value: String(s?.supplier_ID || '').trim(), // ✅ supplierCode as value
          label: String(s?.supplierName || '').trim(),
          supplierCode: String(s?.supplier_ID || '').trim(),
          recordId: s?.record_Id ?? null,
        }))
        .filter((x) => x.value && x.label)
        .sort((a, b) => a.label.localeCompare(b.label));

      setSuppliers(mapped);
      setSelectedSupplier(mapped.length ? mapped[0] : null);
    } catch (e) {
      console.error('Failed to fetch suppliers', e);
      setSuppliers([]);
      setSelectedSupplier(null);
    }
  };

  // ---------- FETCH BRANDS ----------
  const fetchBrands = async () => {
    try {
      const apiService = new ApiService();
      const res = await apiService.get('GET_BRANDS');
      const list = Array.isArray(res?.data?.result) ? res.data.result : [];

      const mapped = list
        .map((b) => ({
          value: String(b?.name || '').trim(), // ✅ brand name as value
          label: String(b?.name || '').trim(),
          brandId: b?.id ?? null,
          code: String(b?.code || '').trim(),
        }))
        .filter((x) => x.value && x.label)
        .sort((a, b) => a.label.localeCompare(b.label));

      setBrands(mapped);
    } catch (e) {
      console.error('Failed to fetch brands', e);
      setBrands([]);
    }
  };

  // ---------- FETCH REPORT ----------
  const fetchData = async (page = pagination.currentPage, size = pagination.pageSize) => {
    if (!selectedSupplier?.supplierCode) {
      setErr('Supplier is required.');
      return;
    }

    setLoading(true);
    setErr('');

    try {
      const apiService = new ApiService();
      const params = new URLSearchParams();

      // ✅ supplierCode
      params.append('SupplierCode', selectedSupplier?.supplierCode || '');

      // ✅ brand name
      params.append('BrandName', selectedBrand?.value || '');

      // ✅ item filter currently removed from UI, send empty
      // params.append('ItemName', '');

      // ✅ optional branch
      params.append('Res_Branch_Id', selectedBranch?.value ?? '');
      params.append('BranchId', selectedBranch?.value ?? '');

      params.append('PageNo', page);
      params.append('PageSize', size);

      const res = await apiService.getApiParamater('GET_STOCK_PREDICTION_REPORT', `?${params.toString()}`);

      const result = Array.isArray(res?.data?.result) ? res.data.result : [];
      const apiPagination = res?.data?.paginationData || {};

      const normalized = result.map((r) => ({
        itemNo: String(r?.itemNo || '—').trim() || '—',
        itemName: String(r?.itemName || '—').trim() || '—',
        quantityInHand: toNumber(r?.quantityInHand),
        qtySoldLast3Months: toNumber(r?.qtySoldLast3Months),
        predictiveInventoryForMonth: toNumber(r?.predictiveInventoryForMonth),
      }));

      setRows(normalized);

      const totalRecords = Number(apiPagination?.totalRecords ?? normalized.length) || 0;
      const pageSize = Number(apiPagination?.pageSize ?? size) || size;
      const rawTotalPages = Number(apiPagination?.totalPages);
      const computedTotalPages =
        Number.isFinite(rawTotalPages) && rawTotalPages > 0 ? rawTotalPages : totalRecords > 0 ? Math.ceil(totalRecords / pageSize) : 0;

      setPagination({
        currentPage: Number(apiPagination?.currentPage ?? page) || 1,
        pageSize,
        totalRecords,
        totalPages: computedTotalPages,
        hasPrevious: typeof apiPagination?.hasPrevious === 'boolean' ? apiPagination.hasPrevious : page > 1,
        hasNext: typeof apiPagination?.hasNext === 'boolean' ? apiPagination.hasNext : computedTotalPages > page,
      });
    } catch (e) {
      console.error(e);
      setErr('Failed to fetch report. Please try again.');
      setRows([]);
      setPagination((prev) => ({
        ...prev,
        totalRecords: 0,
        totalPages: 0,
        hasPrevious: false,
        hasNext: false,
      }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBranches();
    fetchSuppliers();
    fetchBrands();
  }, []);

  useEffect(() => {
    if (selectedSupplier?.supplierCode) {
      fetchData(1, pagination.pageSize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSupplier]);

  // ---------- SORT ----------
  const sortedRows = useMemo(() => {
    const copy = [...rows];
    const { key, dir } = sortState;

    copy.sort((a, b) => {
      const av = a[key];
      const bv = b[key];

      if (Number.isFinite(+av) && Number.isFinite(+bv)) {
        return dir === 'asc' ? +av - +bv : +bv - +av;
      }

      return dir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });

    return copy;
  }, [rows, sortState]);

  const totals = useMemo(
    () => ({
      quantityInHand: sortedRows.reduce((s, r) => s + (Number(r.quantityInHand) || 0), 0),
      qtySoldLast3Months: sortedRows.reduce((s, r) => s + (Number(r.qtySoldLast3Months) || 0), 0),
      predictiveInventoryForMonth: sortedRows.reduce((s, r) => s + (Number(r.predictiveInventoryForMonth) || 0), 0),
    }),
    [sortedRows],
  );

  const onSearch = () => {
    if (!selectedSupplier?.supplierCode) {
      setErr('Supplier is required.');
      return;
    }
    fetchData(1, pagination.pageSize);
  };

  const onSort = (key) => setSortState((prev) => (prev.key !== key ? { key, dir: 'asc' } : { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }));

  const onPageChange = (newPage) => {
    if (newPage < 1) return;
    if (pagination.totalPages && newPage > pagination.totalPages) return;
    fetchData(newPage, pagination.pageSize);
  };

  const onPageSizeChange = (opt) => {
    const newSize = Number(opt?.value || 100);
    fetchData(1, newSize);
  };

  // ---------- CSV EXPORT ----------
  const exportCSV = () => {
    const headers = COLUMNS.map((c) => `"${c.label}"`).join(',');
    const body = sortedRows.map((r) => COLUMNS.map((c) => `"${String(r[c.key] ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');

    const totalsRow = ['""', '"Total"', `"${totals.quantityInHand}"`, `"${totals.qtySoldLast3Months}"`, `"${totals.predictiveInventoryForMonth}"`].join(',');

    const csv = `${headers}\n${body}\n${totalsRow}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `store-predictive-report-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ---------- EXCEL EXPORT ----------
  const exportExcel = () => {
    const excelData = sortedRows.map((r) => ({
      'Item No': r.itemNo,
      'Item Name': r.itemName,
      'Quantity In Hand': r.quantityInHand,
      'Qty Sold Last 3 Months': r.qtySoldLast3Months,
      'Predictive Inventory For Month': r.predictiveInventoryForMonth,
    }));

    excelData.push({
      'Item No': '',
      'Item Name': 'Total',
      'Quantity In Hand': totals.quantityInHand,
      'Qty Sold Last 3 Months': totals.qtySoldLast3Months,
      'Predictive Inventory For Month': totals.predictiveInventoryForMonth,
    });

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    worksheet['!cols'] = [{ wch: 15 }, { wch: 40 }, { wch: 20 }, { wch: 22 }, { wch: 30 }];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Store Predictive Report');

    XLSX.writeFile(workbook, `store-predictive-report-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const pageSizeValue = PAGE_SIZE_OPTIONS.find((x) => x.value === pagination.pageSize) || {
    value: pagination.pageSize,
    label: String(pagination.pageSize),
  };

  return (
    <div className="p-5">
      <button className="addItem-btn mb-3" onClick={() => navigate(-1)}>
        ← Back
      </button>

      <div className="row g-3 align-items-end">
        <div className="col-md-3">
          <label className="form-label">Select Supplier</label>
          <Select
            menuPortalTarget={document.body}
            styles={selectStyles}
            value={selectedSupplier}
            onChange={(opt) => setSelectedSupplier(opt)}
            options={suppliers}
            placeholder="Select Supplier"
            isClearable={false}
          />
        </div>

        <div className="col-md-3">
          <label className="form-label">Select Brand</label>
          <Select
            menuPortalTarget={document.body}
            styles={selectStyles}
            value={selectedBrand}
            onChange={(opt) => setSelectedBrand(opt)}
            options={brands}
            placeholder="All Brands"
            isClearable
          />
        </div>

        <div className="col-md-3">
          <label className="form-label">Select Branch</label>
          <Select
            menuPortalTarget={document.body}
            styles={selectStyles}
            value={selectedBranch}
            onChange={(opt) => setSelectedBranch(opt)}
            options={branches}
            placeholder="All Branches"
            isClearable
          />
        </div>

        <div className="col-md d-flex gap-3 align-items-end">
          <button className="addItem-btn" onClick={onSearch} disabled={loading || !selectedSupplier}>
            {loading ? 'Loading…' : 'Search'}
          </button>

          <button className="btn btn-outline-secondary" onClick={exportCSV} disabled={!sortedRows.length}>
            Export CSV
          </button>

          <button className="btn btn-outline-success" onClick={exportExcel} disabled={!sortedRows.length}>
            Export Excel
          </button>
        </div>
      </div>

      <div className="d-flex justify-content-between align-items-center mt-4 flex-wrap gap-3">
        <div>
          <strong>Total Records:</strong> {pagination.totalRecords}
        </div>

        <div className="d-flex align-items-center gap-2">
          <label className="mb-0">Page Size</label>
          <div style={{ minWidth: 110 }}>
            <Select
              value={pageSizeValue}
              onChange={onPageSizeChange}
              options={PAGE_SIZE_OPTIONS}
              isSearchable={false}
              menuPortalTarget={document.body}
              styles={selectStyles}
            />
          </div>
        </div>
      </div>

      <div className="POsgridshad rounded p-0 mt-3">
        {err && <div className="alert alert-danger m-3">{err}</div>}

        <div className="custom-table-container">
          <table className="custom-table">
            <thead>
              <tr>
                {COLUMNS.map((c) => (
                  <th
                    key={c.key}
                    onClick={() => onSort(c.key)}
                    className={c.align === 'right' ? 'text-end' : ''}
                    style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                  >
                    {c.label}
                    {sortState.key === c.key ? (sortState.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {!loading && sortedRows.length === 0 && (
                <tr>
                  <td colSpan={COLUMNS.length} className="text-center p-4">
                    No data found
                  </td>
                </tr>
              )}

              {!loading &&
                sortedRows.map((r, index) => (
                  <tr key={`${r.itemNo}-${index}`}>
                    <td>{r.itemNo}</td>
                    <td>{r.itemName}</td>
                    <td className="text-end">{numberFmt(r.quantityInHand, 2)}</td>
                    <td className="text-end">{numberFmt(r.qtySoldLast3Months, 2)}</td>
                    <td className="text-end">{numberFmt(r.predictiveInventoryForMonth, 2)}</td>
                  </tr>
                ))}

              {loading && (
                <tr>
                  <td colSpan={COLUMNS.length} className="text-center p-4">
                    Loading…
                  </td>
                </tr>
              )}
            </tbody>

            {sortedRows.length > 0 && (
              <tfoot>
                <tr className="fw-bold bg-light">
                  <td colSpan={2}>Total</td>
                  <td className="text-end">{numberFmt(totals.quantityInHand, 2)}</td>
                  <td className="text-end">{numberFmt(totals.qtySoldLast3Months, 2)}</td>
                  <td className="text-end">{numberFmt(totals.predictiveInventoryForMonth, 2)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-3">
        <div>
          Page <strong>{pagination.currentPage}</strong>
          {pagination.totalPages ? (
            <>
              {' '}
              of <strong>{pagination.totalPages}</strong>
            </>
          ) : null}
        </div>

        <div className="d-flex gap-2">
          <button className="btn btn-outline-secondary" onClick={() => onPageChange(pagination.currentPage - 1)} disabled={loading || !pagination.hasPrevious}>
            Previous
          </button>

          <button className="btn btn-outline-secondary" onClick={() => onPageChange(pagination.currentPage + 1)} disabled={loading || !pagination.hasNext}>
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

const connectMapStateToProps = (state) => ({
  Currency: state.Currency,
  paymentMethod: state.paymentMethod,
  menuItems: state.menuItems,
});

export default connect(connectMapStateToProps)(StorePredictiveReport);
